import { LoanStage } from "@toorak/tc-common-fe-sdk";
import { LoanStatusEnum } from "@toorak/tc-common-util-sdk";
import { isLoanOnHold } from "./AccessManagement";
import { dispatchAndUpdate } from "../create-loan/create-loan.action";
import { TABLE_FUNDING } from "./constants";
import { logger, LoggerLevel } from "./logger";

const canBeMovedToReviewInProgress = (
  loanId: string,
  isDSCR: boolean,
  loanDetails: any,
  loanEvaluationResult: any,
  customData: any = false
) => {
  logger.log(`moveToRIP | Loan ID: ${loanId} | checking if loan can be moved to ReviewInProgress`, LoggerLevel.INFO, {
    loanId,
    isDSCR,
    loanState: loanDetails?.loanState,
    loanStage: loanDetails?.loanStage,
    hasCustomData: !!customData
  }, 'moveToRIP');

  const isTf = loanDetails?.loanDetailId?.fundingType === TABLE_FUNDING;
  const loanStage = loanDetails?.loanStage;
  let teamIndiaTapetoFile =
    loanDetails?.loanDetailId?.loanSummaryId?.teamIndiaTapetoFile;
  let ttfReviewStatus =
    loanDetails?.loanDetailId?.loanSummaryId?.ttfReviewStatus;
  let enhancedReview = loanDetails?.loanDetailId?.loanSummaryId?.enhancedReview;
  if (customData) {
    teamIndiaTapetoFile = customData?.loanSummary?.teamIndiaTapetoFile;
    ttfReviewStatus = customData?.loanSummary?.ttfReviewStatus;
    enhancedReview = customData?.loanSummary?.enhancedReview;
  }

  const isOnHold = isLoanOnHold(loanDetails?.onHoldBy);
  const isInitialReview = loanDetails.loanState === LoanStatusEnum.InitialReview;
  
  let canMoveToReviewInProgress =
    !isOnHold &&
    isInitialReview;

  const loanResult =
    loanEvaluationResult?.loanResult?.[loanId]?.loanCharacterisations;
  const needEnhancedReview = loanResult?.needEnhancedReview;
  const isTeamIndiaTapetoFileYes = ["Yes", "Overridden"].includes(
    teamIndiaTapetoFile
  );
  const isTTFComplete = ttfReviewStatus === "Completed";
  const isEnhancedReviewYes = ["Yes", "Overridden"].includes(enhancedReview);
  
  if (!canMoveToReviewInProgress) {
    logger.log(`moveToRIP | Loan ID: ${loanId} | cannot move to ReviewInProgress - Basic conditions not met`, LoggerLevel.WARN, {
      loanId,
      isOnHold,
      isInitialReview,
      loanState: loanDetails?.loanState
    }, 'moveToRIP');
    return false;
  }

  if (!isDSCR) {
    if (loanStage === LoanStage.pre) {
      canMoveToReviewInProgress =
        (needEnhancedReview !== "Yes" && isTeamIndiaTapetoFileYes) ||
        (needEnhancedReview === "Yes" &&
          isTeamIndiaTapetoFileYes &&
          isEnhancedReviewYes);
    } else if (loanStage === LoanStage.post) {
      canMoveToReviewInProgress =
        (needEnhancedReview !== "Yes" && isTTFComplete) ||
        (needEnhancedReview === "Yes" && isTTFComplete && isEnhancedReviewYes);
    }
  } else {
    const takeoutPartner =
      loanDetails?.thirtyYearLoanInformation?.payload?.takeoutPartnerId;
    const takeoutPartnerStatus =
      loanDetails?.thirtyYearLoanInformation?.payload?.takeoutPartnerStatus;

    canMoveToReviewInProgress =
      takeoutPartner != null &&
      takeoutPartnerStatus !== "PENDING" &&
      isTf &&
      isEnhancedReviewYes;
  }

  logger.log(`moveToRIP | Loan ID: ${loanId} | canBeMovedToReviewInProgress result: ${canMoveToReviewInProgress}`, 
    canMoveToReviewInProgress ? LoggerLevel.INFO : LoggerLevel.WARN, {
    loanId,
    loanStage,
    isDSCR,
    canMoveToReviewInProgress,
    conditions: {
      isTf,
      teamIndiaTapetoFile,
      ttfReviewStatus,
      enhancedReview,
      needEnhancedReview,
      isTeamIndiaTapetoFileYes,
      isTTFComplete,
      isEnhancedReviewYes
    }
  }, 'moveToRIP');

  return canMoveToReviewInProgress;
};

export const moveToReviewInProgress = (
  loanId: string,
  loanStage: LoanStage,
  isDSCR: boolean,
  loanDetails: any,
  loanEvaluationResult: any,
  dispatch: any,
  comments: string,
  customData: any = null
) => {
  logger.log(`moveToRIP | Loan ID: ${loanId} | moveToReviewInProgress called`, LoggerLevel.INFO, {
    loanId,
    loanStage,
    isDSCR,
    loanState: loanDetails?.loanState,
    hasCustomData: !!customData,
    comments
  }, 'moveToRIP');

  const canMove = canBeMovedToReviewInProgress(
    loanId,
    isDSCR,
    loanDetails,
    loanEvaluationResult,
    customData
  );

  if (canMove) {
    logger.log(`moveToRIP | Loan ID: ${loanId} | conditions met, creating dispatchAndUpdate callback`, LoggerLevel.INFO, {
      loanId,
      loanStage,
      targetState: LoanStatusEnum.DueDiligenceReview
    }, 'moveToRIP');

    try {
      const moveToRIPCallBack = dispatchAndUpdate(
        loanId,
        LoanStatusEnum.DueDiligenceReview,
        loanStage,
        dispatch,
        comments,
        true
      );
      
      logger.log(`moveToRIP | Loan ID: ${loanId} | callback created, invoking moveToRIPCallBack`, LoggerLevel.INFO, {
        loanId,
        loanStage
      }, 'moveToRIP');

      moveToRIPCallBack(dispatch).catch((error: any) => {
        logger.log(`moveToRIP | Loan ID: ${loanId} | moveToRIPCallBack execution failed`, LoggerLevel.ERROR, {
          loanId,
          loanStage,
          error: error?.message || String(error),
          stack: error?.stack
        }, 'moveToRIP');
      });
    } catch (error: any) {
      logger.log(`moveToRIP | Loan ID: ${loanId} | failed to create or invoke moveToRIPCallBack`, LoggerLevel.ERROR, {
        loanId,
        loanStage,
        error: error?.message || String(error),
        stack: error?.stack
      }, 'moveToRIP');
    }
  } else {
    logger.log(`moveToRIP | Loan ID: ${loanId} | conditions not met, callback not created`, LoggerLevel.WARN, {
      loanId,
      loanStage,
      loanState: loanDetails?.loanState
    }, 'moveToRIP');
  }
};
